import { Trans, useLingui } from '@lingui/react/macro';
import type { ReactNode } from 'react';

import { Icon } from '../../components/Icon';

import type { ChecklistItem } from './psirtModel';

export interface PsirtChecklistProps {
  title: string;
  items: readonly ChecklistItem[];
  footnote?: ReactNode;
}

/**
 * The live side panel of both forms: which CRA requirement the form already
 * covers. Presence only — the saved verdict comes from the server.
 */
export function PsirtChecklist({ title, items, footnote }: PsirtChecklistProps): ReactNode {
  const { t } = useLingui();
  const mandatory = items.filter((item) => !item.optional);
  const done = mandatory.filter((item) => item.done).length;
  const total = mandatory.length;
  const percent = total === 0 ? 100 : Math.round((done / total) * 100);

  return (
    <aside className="psirt-checklist" aria-label={title}>
      <div className="psirt-checklist-head">
        <strong>{title}</strong>
        <span className="psirt-checklist-count">
          <Trans>{done}/{total} Pflicht</Trans>
        </span>
      </div>
      <div
        className="psirt-meter"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
        aria-label={t`${done} von ${total} Pflichtangaben erfasst`}
      >
        <span style={{ width: `${percent}%` }} data-complete={percent === 100 || undefined} />
      </div>
      <ul>
        {items.map((item) => (
          <li key={item.key} className={item.done ? 'done' : undefined}>
            <span className="psirt-tick" aria-hidden="true">
              {item.done ? <Icon name="check" size={12} /> : null}
            </span>
            <span className="psirt-checklist-label">
              {item.label}
              <span className="visually-hidden">{item.done ? t`, erfasst` : t`, offen`}</span>
              <small>
                {item.reference}
                {item.optional ? <> · <Trans>empfohlen</Trans></> : null}
                {item.inherited ? <> · <Trans>über das Team</Trans></> : null}
              </small>
            </span>
          </li>
        ))}
      </ul>
      {footnote ? <p className="psirt-checklist-foot">{footnote}</p> : null}
    </aside>
  );
}

export default PsirtChecklist;
